/**
 * Bộ biểu tượng của AutoIME.
 *
 * **Tự vẽ chứ không kéo một thư viện về**, để bề dày nét, kiểu đầu nét và cỡ quang học giống hệt
 * nhau ở mọi chỗ — và để dashboard thôi dùng `•`, `✓`, `✗` làm đồ thay thế. Ba ký tự đó thừa hưởng
 * bất cứ font emoji nào hệ điều hành quyết định, và **không bao giờ** khớp với kiểu chữ quanh nó.
 *
 * Một hình học cho tất cả: hộp 24 đơn vị, nét 1.75, đầu và khớp nét bo tròn, màu `currentColor`.
 * Cỡ đặt bằng `className` (`h-4 w-4`), màu đặt bằng màu chữ.
 *
 * Chép từ `Shopify-Colorizeyou/apps/web/src/components/icons.tsx` — tên đổi sang tiếng Việt theo
 * quy ước của repo này, hình vẽ giữ nguyên.
 */

import type { ReactNode } from "react";

type PropBieuTuong = { className?: string };

function Svg({ className = "h-4 w-4", children }: PropBieuTuong & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Tam giác cảnh báo. Dùng cho `Bao muc="canh"`. */
export function BtCanhBao({ className }: PropBieuTuong) {
  return (
    <Svg className={className}>
      <path d="M10.3 3.9 2.4 17.1A2 2 0 0 0 4.1 20h15.8a2 2 0 0 0 1.7-2.9L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </Svg>
  );
}

export function BtTin({ className }: PropBieuTuong) {
  return (
    <Svg className={className}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <path d="M12 8h.01" />
    </Svg>
  );
}

export function BtDung({ className }: PropBieuTuong) {
  return (
    <Svg className={className}>
      <path d="m4.5 12.5 5 5 10-11" />
    </Svg>
  );
}

export function BtDungTron({ className }: PropBieuTuong) {
  return (
    <Svg className={className}>
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12.2 2.4 2.4 4.6-5" />
    </Svg>
  );
}

export function BtDong({ className }: PropBieuTuong) {
  return (
    <Svg className={className}>
      <path d="M6 6 18 18" />
      <path d="M18 6 6 18" />
    </Svg>
  );
}

export function BtMenu({ className }: PropBieuTuong) {
  return (
    <Svg className={className}>
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </Svg>
  );
}

export function BtMuiPhai({ className }: PropBieuTuong) {
  return (
    <Svg className={className}>
      <path d="m9.5 5 7 7-7 7" />
    </Svg>
  );
}

export function BtMuiXuong({ className }: PropBieuTuong) {
  return (
    <Svg className={className}>
      <path d="m5 9.5 7 7 7-7" />
    </Svg>
  );
}

export function BtDongHo({ className }: PropBieuTuong) {
  return (
    <Svg className={className}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5V12l3 1.8" />
    </Svg>
  );
}

export function BtXuHuong({ className }: PropBieuTuong) {
  return (
    <Svg className={className}>
      <path d="M3 17.5 9.5 11l4 4L21 7.5" />
      <path d="M15.5 7.5H21v5.5" />
    </Svg>
  );
}

/** Lỗi. Vòng tròn có dấu chấm than — khác hẳn tam giác cảnh báo, và đó là chủ ý. */
export function BtLoi({ className }: PropBieuTuong) {
  return (
    <Svg className={className}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v5" />
      <path d="M12 16h.01" />
    </Svg>
  );
}

export function BtQuay({ className = "h-4 w-4" }: PropBieuTuong) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={`${className} animate-spin`}
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth={1.75} opacity={0.25} />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
    </svg>
  );
}
